import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { applyForMembership } from '../services/membershipService';
import { ensureMyProfile } from '../services/profileService';

type AuthMode = 'signin' | 'signup';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

const Login: React.FC = () => {
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [wantsToApply, setWantsToApply] = useState(false);
  const [applyMessage, setApplyMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { signIn, signUp, uploadAvatar, updateUserMetadata, user, loading } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (!loading && user) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate]);

  // Clean up preview URL on unmount or when file changes
  useEffect(() => {
    return () => {
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  // Clear errors when switching mode
  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    setError('');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Velg et gyldig bilde (JPEG, PNG, GIF eller WebP).');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('Bildet må være mindre enn 2 MB.');
      return;
    }

    setError('');
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const removeAvatar = () => {
    setAvatarFile(null);
    if (avatarPreview) {
      URL.revokeObjectURL(avatarPreview);
    }
    setAvatarPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (mode === 'signup') {
      // Validate password match
      if (password !== confirmPassword) {
        setError('Passordene er ikke like.');
        return;
      }

      // Validate display name
      if (!displayName.trim()) {
        setError('Skriv inn et visningsnavn.');
        return;
      }
    }

    setIsLoading(true);

    try {
      if (mode === 'signin') {
        const { error: authError } = await signIn(email, password);
        if (authError) {
          if (authError.message.includes('Invalid login credentials')) {
            setError('Ugyldig e-post eller passord. Prøv igjen.');
          } else if (authError.message.includes('Email not confirmed')) {
            setError('Bekreft e-postadressen din før innlogging. Sjekk innboksen.');
          } else {
            setError(authError.message);
          }
        } else {
          navigate('/', { replace: true });
        }
      } else {
        const { error: authError, user: newUser } = await signUp(email, password, {
          display_name: displayName.trim(),
        });
        if (authError) {
          if (authError.message.includes('already registered')) {
            setError('En konto med denne e-posten finnes allerede. Prøv å logge inn i stedet.');
          } else {
            setError(authError.message);
          }
        } else {
          // If an avatar file was selected, upload it after sign-up
          if (avatarFile && newUser) {
            const { url, error: uploadError } = await uploadAvatar(newUser.id, avatarFile);
            if (url && !uploadError) {
              await updateUserMetadata({ avatar_url: url });
            }
            // Don't block sign-up if avatar upload fails — they can update it later
          }
          // Ensure a profiles row exists right away (covers no-email-confirm flows)
          if (newUser) {
            await ensureMyProfile(newUser);
          }
          // If the user checked 'apply for membership' and wrote a message, submit the request
          if (wantsToApply && applyMessage.trim() && newUser) {
            await applyForMembership(newUser.id, applyMessage.trim());
          }
          // Auto-login successful (email confirm disabled) — redirect to home
          navigate('/', { replace: true });
        }
      }
    } catch (err) {
      setError('En uventet feil oppstod. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  };

  const isSignUp = mode === 'signup';

  return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute top-1/3 right-1/4 w-80 h-80 pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2 tracking-tight">
            {isSignUp ? 'Bli med i Smuglergods' : 'Velkommen tilbake'}
          </h1>
          <p className="text-gray-400 text-lg">
            {isSignUp ? 'Opprett konto' : 'Logg inn på kontoen din'}
          </p>
        </div>

        {/* Auth Card */}
        <div className="card-glow p-8">
          {/* Mode Toggle Tabs */}
          <div className="flex mb-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <button
              type="button"
              onClick={() => switchMode('signin')}
              className={`flex-1 pb-3 text-sm font-bold transition-all ${
                !isSignUp
                  ? 'text-white border-b-2 border-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              Logg inn
            </button>
            <button
              type="button"
              onClick={() => switchMode('signup')}
              className={`flex-1 pb-3 text-sm font-bold transition-all ${
                isSignUp
                  ? 'text-white border-b-2 border-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              Registrer
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error Message */}
            {error && (
              <div className="bg-red-900 bg-opacity-30 border border-red-500 border-opacity-50 text-red-300 px-4 py-3 rounded text-sm">
                {error}
              </div>
            )}

            {/* Display Name (sign-up only) */}
            {isSignUp && (
              <div>
                <label htmlFor="displayName" className="block text-sm font-medium text-gray-300 mb-2">
                  Visningsnavn
                </label>
                <input
                  id="displayName"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  placeholder="Navnet ditt"
                  className="input-glow w-full px-4 py-3 rounded-lg"
                />
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                E-postadresse
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="input-glow w-full px-4 py-3 rounded-lg"
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Passord
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="••••••••"
                className="input-glow w-full px-4 py-3 rounded-lg"
              />
              {isSignUp && (
                <p className="text-gray-500 text-xs mt-1">Minst 6 tegn</p>
              )}
            </div>

            {/* Confirm Password (sign-up only) */}
            {isSignUp && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                  Bekreft passord
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="••••••••"
                  className="input-glow w-full px-4 py-3 rounded-lg"
                />
              </div>
            )}

            {/* Profile Picture Upload (sign-up only) */}
            {isSignUp && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Profilbilde <span className="text-gray-500">(valgfritt)</span>
                </label>
                <div className="flex items-center gap-4">
                  {/* Preview */}
                  <div className="shrink-0">
                    {avatarPreview ? (
                      <div className="relative group">
                        <img
                          src={avatarPreview}
                          alt="Avatar preview"
                          className="w-16 h-16 rounded-full object-cover" style={{ border: '1px solid rgba(255,255,255,0.2)' }}
                        />
                        <button
                          type="button"
                          onClick={removeAvatar}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Fjern"
                        >
                          &times;
                        </button>
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-full border-2 border-dashed border-gray-600 flex items-center justify-center">
                        <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {/* Upload button */}
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="avatarFile"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="btn-secondary py-2 px-4 text-sm font-medium"
                    >
                      {avatarFile ? 'Bytt bilde' : 'Velg bilde'}
                    </button>
                    <p className="text-gray-500 text-xs mt-1">JPEG, PNG, GIF eller WebP. Maks 2 MB.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Membership Application (sign-up only — optional) */}
            {isSignUp && (
              <div className="p-4 space-y-3" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="flex items-start gap-3">
                  <input
                    id="wantsToApply"
                    type="checkbox"
                    checked={wantsToApply}
                    onChange={(e) => setWantsToApply(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-600 bg-gray-800 text-neon-green focus:ring-neon-green focus:ring-offset-0 accent-emerald-500"
                  />
                  <label htmlFor="wantsToApply" className="text-sm text-gray-300 cursor-pointer select-none">
                    <span className="text-white font-semibold">Søk om Smugler-medlemskap</span>
                    <span className="block text-gray-500 text-xs mt-0.5">Valgfritt — en admin vil gjennomgå søknaden din.</span>
                  </label>
                </div>
                {wantsToApply && (
                  <div>
                    <label htmlFor="applyMessage" className="block text-xs text-gray-400 mb-1">
                      Fortell kort om deg selv og hvorfor du ønsker å bli Smugler
                    </label>
                    <textarea
                      id="applyMessage"
                      value={applyMessage}
                      onChange={(e) => setApplyMessage(e.target.value)}
                      placeholder="Skriv litt om deg selv..."
                      rows={3}
                      maxLength={500}
                      className="input-glow w-full px-4 py-2 resize-none text-sm"
                    />
                    <p className="text-gray-600 text-xs mt-1 text-right">{applyMessage.length}/500</p>
                  </div>
                )}
              </div>
            )}

            {/* Forgot Password Link (sign-in only) */}
            {!isSignUp && (
              <div className="flex justify-end">
                <button type="button" className="text-sm link-primary transition-colors">
                  Glemt passord?
                </button>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary py-3 px-6 rounded-lg text-base font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {isSignUp ? 'Oppretter konto...' : 'Logger inn...'}
                </span>
              ) : (
                isSignUp ? 'Opprett konto' : 'Logg inn'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="my-6 flex items-center">
            <div className="flex-grow" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}></div>
            <span className="px-4 text-sm text-gray-500">eller</span>
            <div className="flex-grow" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}></div>
          </div>

          {/* Toggle between modes */}
          <p className="text-center text-gray-400 text-sm">
            {isSignUp ? (
              <>
                Har du allerede konto?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signin')}
                  className="link-primary font-medium transition-colors"
                >
                  Logg inn
                </button>
              </>
            ) : (
              <>
                Har du ikke konto?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signup')}
                  className="link-primary font-medium transition-colors"
                >
                  Registrer
                </button>
              </>
            )}
          </p>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link to="/" className="link-secondary text-sm transition-colors">
            &larr; Tilbake til hjem
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
